'use client';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { elowenClient } from "../elowenClient";
import { QUERY_KEYS } from "../queries";
import type { CliSettings, TerminalSettings, TerminalSettingsSnapshot, PermissionSettings, NavLayout, AlertSettings, AlertsResponse } from "../types";
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { ElowenApiError } from '../elowenClient';
import { authGeneration } from '../token';

/** A personal-settings write refused because its revision is stale: the daemon answers 409 with the snapshot it
 *  holds now. Other 409s (an unreadable stored setting, an identity owned by another account) carry none. */
export function isSettingsRevisionConflict(error: unknown): error is ElowenApiError & { details: { current: unknown } } {
  return error instanceof ElowenApiError && error.status === 409 && !!error.details?.current;
}

/** Adopt the canonical snapshot a personal-settings conflict carries; other errors stay with the caller. */
export function adoptCurrentSettings(qc: QueryClient, key: QueryKey, error: unknown): boolean {
  if (!isSettingsRevisionConflict(error)) return false;
  qc.setQueryData(key, error.details.current);
  return true;
}

export function useSaveMyCliSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<CliSettings>) => elowenClient.saveMyCliSettings(patch, qc.getQueryData<CliSettings>(['my-cli-settings'])?.revision),
    onSuccess: (saved) => qc.setQueryData(['my-cli-settings'], saved),
    onError: (error) => { adoptCurrentSettings(qc, ['my-cli-settings'], error); },
  });
}

export function useSaveMyTerminalSettings() {
  const qc = useQueryClient();
  return useMutation({
    onMutate: () => ({ issuedIn: authGeneration() }),
    mutationFn: (patch: Partial<TerminalSettings>) => elowenClient.saveMyTerminalSettings(patch, qc.getQueryData<TerminalSettingsSnapshot>(['my-terminal-settings'])?.revision),
    onSuccess: (saved, _patch, context) => {
      if (context?.issuedIn === authGeneration()) qc.setQueryData(['my-terminal-settings'], saved);
    },
    onError: (error, _patch, context) => {
      if (context?.issuedIn === authGeneration()) adoptCurrentSettings(qc, ['my-terminal-settings'], error);
    },
  });
}

export function useSaveMyPermissions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<PermissionSettings>) => elowenClient.saveMyPermissions(patch, qc.getQueryData<PermissionSettings>(['my-permissions'])?.revision),
    onSuccess: (saved) => qc.setQueryData(['my-permissions'], saved),
    onError: (error) => { adoptCurrentSettings(qc, ['my-permissions'], error); },
  });
}

/** Save the navigation layout. The route answers with the whole sanitized layout, so the cache is written
 *  from the response instead of invalidated — the menu must not flicker back to its old arrangement while
 *  a refetch is in flight. */
export function useSaveMyNavSettings() {
  const qc = useQueryClient();
  const key = ['my-nav-settings'];
  const lastToSettle = () => qc.isMutating({ mutationKey: key }) <= 1;
  return useMutation({
    mutationKey: key,
    scope: { id: 'my-nav-settings' },
    mutationFn: (patch: Partial<NavLayout>) => elowenClient.saveMyNavSettings(patch, qc.getQueryData<NavLayout>(key)?.revision),
    // Earlier saves advance only the CAS revision: a queued apply already owns the optimistic layout.
    onSuccess: (layout) => {
      qc.setQueryData<NavLayout>(key, current => lastToSettle() ? layout : { ...current!, revision: layout.revision });
    },
    onError: (error) => {
      if (!lastToSettle()) {
        if (error instanceof ElowenApiError && error.status === 409 && error.details?.current) {
          const canonical = error.details.current as NavLayout;
          qc.setQueryData<NavLayout>(key, current => ({ ...current!, revision: canonical.revision }));
        }
        return;
      }
      // Only the final refusal may replace the optimistic layout or refetch after an ambiguous failure.
      if (!adoptCurrentSettings(qc, key, error)) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

/** The three alert writes share cancellation, exact snapshot rollback and freshness. */
function useOptimisticAlertMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  transform: (previous: AlertsResponse, variables: TVariables) => AlertsResponse,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onMutate: async (variables: TVariables) => {
      const issuedIn = authGeneration();
      await qc.cancelQueries({ queryKey: QUERY_KEYS.alerts });
      if (issuedIn !== authGeneration()) throw new ElowenApiError('session changed before alert mutation', 401);
      const previous = qc.getQueryData<AlertsResponse>(QUERY_KEYS.alerts);
      if (previous) qc.setQueryData<AlertsResponse>(QUERY_KEYS.alerts, transform(previous, variables));
      return { previous, issuedIn };
    },
    onError: (_error, _variables, context) => {
      if (context?.issuedIn === authGeneration() && context.previous) qc.setQueryData(QUERY_KEYS.alerts, context.previous);
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context?.issuedIn === authGeneration()) void qc.invalidateQueries({ queryKey: QUERY_KEYS.alerts });
    },
  });
}

/** Acknowledge one alert and decrement only a genuinely unread row. */
export function useMarkAlertRead() {
  return useOptimisticAlertMutation((id: number) => elowenClient.markAlertRead(id), (previous, id) => {
    const row = previous.alerts.find((alert) => alert.id === id);
    return {
      alerts: previous.alerts.map((alert) => (alert.id === id && !alert.readAt ? { ...alert, readAt: new Date().toISOString() } : alert)),
      unread: row && !row.readAt ? Math.max(0, previous.unread - 1) : previous.unread,
    };
  });
}

/** Acknowledge every open alert, keeping existing acknowledgement times. */
export function useMarkAllAlertsRead() {
  return useOptimisticAlertMutation((_variables: void) => elowenClient.markAllAlertsRead(), (previous) => ({
    alerts: previous.alerts.map((alert) => (alert.readAt ? alert : { ...alert, readAt: new Date().toISOString() })),
    unread: 0,
  }));
}

/** Dismiss one row without clearing its producer's condition. */
export function useDismissAlert() {
  return useOptimisticAlertMutation((id: number) => elowenClient.dismissAlert(id), (previous, id) => {
    const row = previous.alerts.find((alert) => alert.id === id);
    return {
      alerts: previous.alerts.filter((alert) => alert.id !== id),
      unread: row && !row.readAt ? Math.max(0, previous.unread - 1) : previous.unread,
    };
  });
}

/** Save the caller's account-wide muted notification kinds. Same revision pattern as the other personal-settings
 *  saves: the editor's opening `revision` rides along, the saved answer replaces the cache, and a 409 means the
 *  server's `current` wins so the form re-renders on the canonical value instead of repeating the 409. */
export function useSaveMyAlertSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ muted, expectedRevision }: { muted: AlertSettings['muted']; expectedRevision: number }) =>
      elowenClient.saveMyAlertSettings(muted, expectedRevision),
    onSuccess: (saved) => { qc.setQueryData(['my-alert-settings'], saved); },
    onError: (error) => {
      if (!adoptCurrentSettings(qc, ['my-alert-settings'], error)) void qc.invalidateQueries({ queryKey: ['my-alert-settings'] });
    },
  });
}
